<script setup lang="ts">
    import { ref, watch, onMounted, computed, inject } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import type { RouteLocationRaw } from 'vue-router';
    import List from '@shared/components/List/List.vue';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import ContactFilter from './components/ContactFilter.vue';
    import { deleteContacts } from './ContactList.access';
    import type { ContactsResponse } from './ContactList.access';
    import type { Contact } from '../Contact/ContactTypes';
    import { setupTags } from '@shared/components/Tags/Tags';
    import type { OrderByDirection, MoreAction } from '@shared/components/List/ListTypes';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { FeatureService } from '@shared/directives/Feature.service';
    import Modal from '@shared/components/Modal.vue';
    import ContactFilterAlert from './components/ContactFilterAlert.vue';
    import SingleSelect from '@shared/components/SingleSelect.vue';
    import FormRadioGroup from '@shared/components/Form/FormRadioGroup.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import ContactListItem from './ContactListItem.vue';
    import ContactAdditionalFieldsDropdown from './components/ContactAdditionalFieldsDropdown.vue';
    import ContactExportDropdown from './components/ContactExportDropdown.vue';
    import { useContactAdditionalFields } from './useContactAdditionalFields';
    import { loadLists } from '@views/Lists/Lists.access';
    import { List as ListType } from '@views/Lists/ListsTypes';

    const props = defineProps<{
        title?: string;
        description?: string;
        countLabel?: string;
        extraOrderOptions?: { key: string; display: string; type?: string }[];
        extraCountFields?: { key: keyof Contact; label: string; timestampKey?: keyof Contact; route?: (contact: Contact) => RouteLocationRaw }[];
        showOpenClickFilter?: boolean;
        contextExtra?: string;
        hideAddedDateTime?: boolean;
        isCheckable?: boolean;
        showingUnsubscribed?: boolean;
        fetchContacts: (skip: number, pageSize: number, extra: string) => Promise<ContactsResponse>;
        exportExcel?: (extra: string) => Promise<{ Url: string }>;
        exportCsv?: (extra: string) => Promise<{ Url: string }>;
        exportToNewList?: (extra: string) => Promise<{ ListId: string }>;
        exportToExistingList?: (extra: string, targetListId: string) => Promise<{ ListId: string }>;
        sendExcelByMail?: (extra: string) => Promise<void>;
        deleteContactsFn?: (contactIds: string[]) => Promise<void>;
        bounceContactFn?: (contactId: string) => Promise<void>;
        unsubscribeContactFn?: (contactId: string) => Promise<void>;
        contactRoute?: (contactId: string) => RouteLocationRaw;
        isAnswerInAutoFilter?: (value: string) => boolean;
        isAnswerInFilter?: (value: string) => boolean;
        moreActions?: MoreAction[];
        /** Target endpoint parses OData v4 (Microsoft.AspNetCore.OData) — guid/datetime literals must be bare. */
        odataV4?: boolean;
        /** Hides the tags and subscriptions filter sections, e.g. when the underlying endpoint doesn't support them. */
        scalarOnly?: boolean;
    }>();

    const emit = defineEmits<{
        moreActionClick: [key: string];
        toggleAnswerFilter: [value: string];
    }>();

    const route = useRoute();
    const router = useRouter();
    const tags = setupTags();

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const featureService: FeatureService = injectorAccess.injector.get(FeatureService);
    const isBackOffice = computed(() => featureService.hasBackOfficeAccess());

    // --- Order options ---

    const defaultOrderOptions = [
        { key: 'Email', display: $Resources.ContactsSortByEmailLabel },
        { key: 'FirstName,LastName', display: $Resources.ContactsSortByNameLabel },
        { key: 'Created', display: $Resources.GenericCreatedLabel },
        { key: 'LastModified', display: $Resources.LastModifiedLabel },
    ];

    const PAGE_SIZE = 25;
    const loading = ref(false);
    const exporting = ref(false);
    const deleting = ref(false);
    const checkedItems = ref<string[]>([]);
    const contacts = ref<Contact[]>([]);
    const totalCount = ref(0);
    const openClickFilter = ref<'Both' | 'OpenOnly' | 'ClickOnly'>('Both');
    const filter = ref('');
    const orderBy = ref(defaultOrderOptions[0].key);
    const orderDirection = ref<OrderByDirection>('asc');
    const advancedFilter = ref('');
    const filterRef = ref<InstanceType<typeof ContactFilter>>();
    const showExportToExistingListModal = ref(false);
    const selectedExportListId = ref<string | null>(null);
    const contactLists = ref<ListType[] | null>(null);

    const allLoaded = computed(() => contacts.value.length >= totalCount.value);

    // countLabel describes the unfiltered set, so fall back to the row count once the list is narrowed
    const hasActiveFilter = computed(
        () => !!filter.value.trim() || !!advancedFilter.value || (!!props.showOpenClickFilter && openClickFilter.value !== 'Both')
    );

    const orderOptions = computed(() => {
        const base = props.extraOrderOptions?.length ? [...defaultOrderOptions, ...props.extraOrderOptions] : defaultOrderOptions;
        const fieldOptions = selectedFields.value.map((f) => ({ key: f.name, display: f.displayName }));
        return [...base, ...fieldOptions];
    });

    // --- Data-driven field configs ---

    // timestampKey, where present, is shown directly under that count in the same box.
    const countFields = computed<{ key: keyof Contact; label: string; timestampKey?: keyof Contact; route?: (contact: Contact) => RouteLocationRaw }[]>(() => [
        { key: 'SentCount', label: $Resources.ContactSentCountLabel },
        { key: 'ReceivedCount', label: $Resources.ContactRecipientCountLabel, timestampKey: 'FirstReceived' },
        { key: 'OpenCount', label: $Resources.ContactOpenedCountLabel, timestampKey: 'FirstOpen' },
        { key: 'ClickCount', label: $Resources.ContactClickedCountLabel, timestampKey: 'FirstClick' },
        { key: 'ShareCount', label: $Resources.ContactSharedCountLabel },
        ...(props.extraCountFields ?? []),
    ]);

    const { availableFields, selectedFieldNames, selectedFields, toggleField } = useContactAdditionalFields(contextAccess, {
        hideAddedDateTime: props.hideAddedDateTime,
        showingUnsubscribed: props.showingUnsubscribed,
        showingBounceData: true,
    });

    // --- API ---

    function buildOrderByClause(): string {
        const key = orderBy.value;
        if (!key) return '';
        const dir = orderDirection.value;
        if (key === 'Name') return `FirstName ${dir},LastName ${dir}`;
        return `${key} ${dir}`;
    }

    function buildExtra(): string {
        const parts: string[] = [];
        let odataFilter = '';

        if (props.contextExtra) {
            parts.push(props.contextExtra);
        }

        if (props.showOpenClickFilter) {
            if (openClickFilter.value === 'OpenOnly') odataFilter = 'ClickCount+eq+0';
            if (openClickFilter.value === 'ClickOnly') odataFilter = 'OpenCount+eq+0';
        }

        if (advancedFilter.value) odataFilter = appendFilter(odataFilter, advancedFilter.value);

        const trimmed = filter.value.trim();
        if (trimmed) {
            const term = encodeURIComponent(trimmed.toLowerCase());
            // SMS numbers are stored as digits with an optional leading '+' (TextHelper.CleanSmsNumber); only search them when the input could be one.
            // Many flows store numbers internationally (+46701234567), so a local leading 0 is dropped to match both formats.
            const smsCandidate = trimmed.replace(/[\s-]/g, '').replace(/^0/, '');
            // Require a few digits so short inputs don't match nearly every number.
            const smsTerm = /^\+?\d{4,}$/.test(smsCandidate) ? encodeURIComponent(smsCandidate) : '';
            let searchFilter = `(indexof(tolower(Email),'${term}') ge 0)+or+(indexof(tolower(FirstName),'${term}') ge 0)+or+(indexof(tolower(LastName),'${term}') ge 0)`;
            if (smsTerm) searchFilter += `+or+(indexof(SmsNumber,'${smsTerm}') ge 0)`;

            const spaceIndex = trimmed.indexOf(' ');
            if (spaceIndex > 0) {
                const firstPart = encodeURIComponent(trimmed.substring(0, spaceIndex).trim().toLowerCase());
                const secondPart = encodeURIComponent(
                    trimmed
                        .substring(spaceIndex + 1)
                        .trim()
                        .toLowerCase()
                );
                if (firstPart && secondPart) {
                    searchFilter += `+or+((indexof(tolower(FirstName),'${firstPart}') ge 0)+and+(indexof(tolower(LastName),'${secondPart}') ge 0))`;
                }
            }

            odataFilter = appendFilter(odataFilter, searchFilter);
        }

        if (odataFilter) parts.push(`$filter=${odataFilter}`);

        const order = buildOrderByClause();
        if (order) parts.push(`$orderby=${order}`);

        return parts.length ? '&' + parts.join('&') : '';
    }

    function appendFilter(existing: string, addition: string): string {
        return existing ? `(${existing})+and+(${addition})` : addition;
    }

    // Guards against out-of-order responses (e.g. an earlier, unfiltered fetch resolving after a later,
    // filtered one) clobbering the latest results — only the response to the most recent call is applied.
    let requestSeq = 0;

    async function loadContacts(append = false) {
        const seq = ++requestSeq;
        loading.value = true;
        try {
            const skip = append ? contacts.value.length : 0;
            const result = await props.fetchContacts(skip, PAGE_SIZE, buildExtra());
            if (seq !== requestSeq) return;
            contacts.value = append ? [...contacts.value, ...result.Results] : result.Results;
            totalCount.value = result.Count;
        } catch {
            if (seq !== requestSeq) return;
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            if (seq === requestSeq) loading.value = false;
        }
    }

    // --- Export ---

    async function exportExcel() {
        exporting.value = true;
        try {
            const result = await props.exportExcel?.(buildExtra());
            if (result?.Url) window.location.href = result.Url;
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            exporting.value = false;
        }
    }

    async function exportCsv() {
        exporting.value = true;
        try {
            const result = await props.exportCsv?.(buildExtra());
            if (result?.Url) window.location.href = result.Url;
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            exporting.value = false;
        }
    }

    async function sendExcelByMail() {
        exporting.value = true;
        try {
            await props.sendExcelByMail?.(buildExtra());
            bsAlert({ title: $Resources.ContactsGetExcelFileAlertSuccessHeading, content: $Resources.ContactsGetExcelFileAlertSuccessBody });
        } catch {
            bsAlert({
                type: AlertTypes.Danger,
                title: $Resources.ContactsGetExcelFileAlertErrorHeading,
                content: $Resources.ContactsGetExcelFileAlertErrorBody,
            });
        } finally {
            exporting.value = false;
        }
    }

    async function exportToNewList() {
        exporting.value = true;
        try {
            const result = await props.exportToNewList?.(buildExtra());
            if (result?.ListId) router.push(`/Lists/${result.ListId}`);
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            exporting.value = false;
        }
    }

    // --- Event handlers ---

    function onAdvancedFilterChange(odataFilter: string) {
        advancedFilter.value = odataFilter;
        loadContacts();
    }

    function onFilterUpdate(newFilter: string) {
        filter.value = newFilter;
        loadContacts();
    }

    function onOrderUpdate(key: string, direction: OrderByDirection) {
        orderBy.value = key;
        orderDirection.value = direction;
        loadContacts();
    }

    function onLoadMore() {
        loadContacts(true);
    }

    // --- Selection & contact actions ---

    function onCheckedItemsUpdate(newVal: (string | number)[]) {
        checkedItems.value = newVal as string[];
    }

    function onClearSelected() {
        checkedItems.value = [];
    }

    async function onDeleteSelected() {
        if (checkedItems.value.length === 0) return;
        deleting.value = true;
        try {
            await (props.deleteContactsFn ?? deleteContacts)(checkedItems.value);
            bsAlert({ type: AlertTypes.Success });
            checkedItems.value = [];
            loadContacts();
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            deleting.value = false;
        }
    }

    async function bounceContact(contactId: string) {
        try {
            await props.bounceContactFn?.(contactId);
            bsAlert({ type: AlertTypes.Success, content: '[Bounced]' });
            loadContacts();
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        }
    }

    async function unsubscribeContactAction(contactId: string) {
        try {
            await props.unsubscribeContactFn?.(contactId);
            bsAlert({ type: AlertTypes.Success, content: '[Unsubscribed]' });
            loadContacts();
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        }
    }

    // --- Watchers ---

    watch(openClickFilter, () => loadContacts());

    onMounted(() => {
        loadContacts();
    });

    const openExportToExistingListModal = async () => {
        if (!contactLists.value) {
            contactLists.value = (await loadLists()) as ListType[];
        }
        selectedExportListId.value = null;
        showExportToExistingListModal.value = true;
    };

    const confirmExportToExistingList = async () => {
        if (!selectedExportListId.value) return;
        showExportToExistingListModal.value = false;
        exporting.value = true;
        try {
            const result = await props.exportToExistingList?.(buildExtra(), selectedExportListId.value);
            if (result?.ListId) router.push(`/Lists/${result.ListId}`);
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            exporting.value = false;
        }
    };

    const mappedContactLists = computed(() => {
        return (
            contactLists.value
                ? contactLists.value.map((list) => ({
                      ListId: list.ListId,
                      Name: list.Name,
                  }))
                : []
        ) as Record<string, unknown>[];
    });

    function pushContact(contact: Contact) {
        if (contacts.value.some((c) => c.ContactId === contact.ContactId)) return;
        contacts.value = [...contacts.value, contact];
        totalCount.value += 1;
    }

    function updateContact(contact: Contact) {
        const idx = contacts.value.findIndex((c) => c.ContactId === contact.ContactId);
        if (idx === -1) return;
        contacts.value = [...contacts.value.slice(0, idx), contact, ...contacts.value.slice(idx + 1)];
    }

    defineExpose({ pushContact, updateContact, refresh: loadContacts, totalCount });
</script>

<template>
    <List
        :items="contacts"
        :loading="loading"
        :allLoaded="allLoaded"
        :totalItemCount="totalCount"
        :countLabel="hasActiveFilter ? undefined : countLabel"
        :title="title"
        :orderOptions="orderOptions"
        :orderByDirection="orderDirection"
        :orderByValue="orderBy"
        :filterRateLimit="350"
        :serverSideFilter="true"
        :perPage="PAGE_SIZE"
        :checkedItems="isCheckable ? checkedItems : null"
        :deleteConfirmation="true"
        :deleting="deleting"
        itemId="ContactId"
        :recycleManager="true"
        :moreActions="moreActions"
        @update:filter="onFilterUpdate"
        @update:order="onOrderUpdate"
        @update:checkedItems="onCheckedItemsUpdate"
        @clearSelected="onClearSelected"
        @deleteSelected="onDeleteSelected"
        @loadMore="onLoadMore"
        @moreActionClick="(key) => emit('moreActionClick', key)">
        <template #header>
            <p v-if="description" v-html="description"></p>

            <FormRadioGroup
                v-if="showOpenClickFilter"
                :legend="null"
                :options="[
                    { label: $Resources.OpenOrClickFilterBothLabel, value: 'Both' },
                    { label: $Resources.OpenOrClickFilterOpenOnlyLabel, value: 'OpenOnly' },
                    { label: $Resources.OpenOrClickFilterClickOnlyLabel, value: 'ClickOnly' },
                ]"
                :switchStyle="SwitchStyle.Button"
                v-model="openClickFilter"
                class="btn-group btn-group-sm m-sm-b" />
        </template>
        <template #toolbar>
            <template v-if="checkedItems.length === 0">
                <!-- Filter -->
                <BSDropdownButton menuClass="dropdown-menu-right" :closeOnClickInside="false" :disabled="loading" :disableCloseOnClickClassList="['drop-mask']">
                    <template #toggle>
                        <span class="glyphicon glyphicon-filter m-xxs-r"></span>
                        {{ $Resources.FilterHeading }}
                    </template>
                    <template #content>
                        <li class="dropdown-menu-content" :class="filterRef?.filterCount && 'has-selected'">
                            <ContactFilter
                                ref="filterRef"
                                :availableFields="availableFields"
                                :odataV4="props.odataV4"
                                :scalarOnly="props.scalarOnly"
                                @change="onAdvancedFilterChange" />
                        </li>
                    </template>
                </BSDropdownButton>

                <!-- Additional fields -->
                <ContactAdditionalFieldsDropdown :availableFields="availableFields" :selectedFieldNames="selectedFieldNames" @toggleField="toggleField" />

                <!-- Export -->
                <ContactExportDropdown
                    :disabled="contacts.length === 0 || exporting"
                    @exportExcel="exportExcel"
                    @exportCsv="exportCsv"
                    @exportToNewList="exportToNewList"
                    @exportToExistingList="openExportToExistingListModal"
                    @sendExcelByMail="sendExcelByMail" />
            </template>
        </template>
        <template #alerts>
            <ContactFilterAlert :filterRef="filterRef" :totalCount="totalCount" />
        </template>
        <template #item="{ item: contact }">
            <ContactListItem
                :contact="contact"
                :isBackOffice="isBackOffice"
                :countFields="countFields"
                :selectedFields="selectedFields"
                :selectedTagIds="tags.state.selectedIds"
                :contactTo="contactRoute ? contactRoute(contact.ContactId) : undefined"
                :isAnswerInAutoFilter="isAnswerInAutoFilter"
                :isAnswerInFilter="isAnswerInFilter"
                @bounceContact="bounceContact"
                @unsubscribeContact="unsubscribeContactAction"
                @toggleTag="(tag) => tags.toggle(tag)"
                @toggleAnswerFilter="(value) => emit('toggleAnswerFilter', value)" />
        </template>
    </List>

    <Modal :show="showExportToExistingListModal" :title="$Resources.ExportToExistingListTitle" @close="showExportToExistingListModal = false">
        <template #content>
            <SingleSelect
                :items="mappedContactLists"
                :modelValue="selectedExportListId"
                selectProp="ListId"
                displayProps="Name"
                :label="$Resources.SelectListLabel"
                @update:modelValue="(val) => (selectedExportListId = val)" />
        </template>
        <template #button>
            <button type="button" class="btn btn-primary" :disabled="!selectedExportListId || exporting" @click="confirmExportToExistingList">
                {{ $Resources.ExportButton }}
            </button>
        </template>
    </Modal>
</template>
